import * as React from 'react';
import {
  autoUpdate,
  flip,
  hide,
  offset as setOffset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  Placement,
} from '@floating-ui/react';
import cx from 'classnames';

import Portal from '../HOC/Portal';

type PropsSpread<L, R> = R & Pick<L, Exclude<keyof L, keyof R>>;

interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  offset?: number;
  onOpenChange?: (open: boolean) => void;
}

function usePopover({
  initialOpen = false,
  offset = 5,
  placement = 'bottom-start',
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: PopoverOptions) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      setOffset(offset),
      flip({
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift({ padding: 5 }),
      hide(),
    ],
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
    }),
    [open, setOpen, interactions, data, modal]
  );
}

usePopover.defaultProps = {
  offset: 5,
};

type ContextType =
  | (ReturnType<typeof usePopover> & {
      // * Other custom props
    })
  | null;

const PopoverContext = React.createContext<ContextType>(null);

const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

interface PopoverProps extends PopoverOptions {
  children:
    | React.ReactNode
    | ((props: PopoverChildProps) => React.ReactElement);
  className?: string;
}

function PopoverContainer({
  children,
  modal = false,
  className,
  ...restOptions
}: PopoverProps) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });

  return (
    <PopoverContext.Provider value={popover}>
      <div className={cx(className && className)}>
        {typeof children === 'function'
          ? children({
              onClose: () => popover?.setOpen(false),
              open: popover?.open,
            })
          : React.Children.map<React.ReactNode, React.ReactNode>(
              children,
              (child) => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(
                    child as React.ReactElement<PopoverChildProps>,
                    {
                      onClose: () => popover?.setOpen(false),
                      open: popover?.open,
                    }
                  );
                }
              }
            )}
      </div>
    </PopoverContext.Provider>
  );
}

type PopoverTriggerProps = PropsSpread<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  { children: React.ReactNode; asChild?: boolean }
>;

const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  function PopoverTrigger({ children, asChild = false, ...props }, propRef) {
    const context = usePopoverContext();
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children,
        context.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          'data-state': context.open ? 'open' : 'closed',
        })
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        // The user can style the trigger based on the state
        data-state={context.open ? 'open' : 'closed'}
        {...context.getReferenceProps(props)}
      >
        {children}
      </button>
    );
  }
);

type PopoverChildProps = {
  onClose: () => void;
  open: boolean;
};

type PopoverContentProps = PropsSpread<
  React.HTMLAttributes<HTMLDivElement>,
  {
    children?:
      | React.ReactNode
      | JSX.Element
      | JSX.Element[]
      | ((props: PopoverChildProps) => JSX.Element | React.ReactNode);
    asChild?: boolean;
    portalTarget?: Element | DocumentFragment | null;
  }
>;

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent({ portalTarget, children, ...props }, propRef) {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!floatingContext.open) return null;

    return (
      <Portal portalTarget={portalTarget}>
        <div
          ref={ref}
          style={{
            ...context.floatingStyles,
            ...props.style,
            zIndex: 999,
            visibility: context.middlewareData?.hide?.referenceHidden
              ? 'hidden'
              : 'visible',
          }}
          {...context.getFloatingProps(props)}
        >
          {typeof children === 'function'
            ? children({
                onClose: () => context?.setOpen(false),
                open: context?.open,
              })
            : React.Children.map<React.ReactNode, React.ReactNode>(
                children,
                (child, key) => {
                  if (React.isValidElement(child)) {
                    return React.cloneElement(
                      child as React.ReactElement<PopoverChildProps>,
                      {
                        onClose: () => context?.setOpen(false),
                        open: context?.open,
                        key,
                      }
                    );
                  }
                }
              )}
        </div>
      </Portal>
    );
  }
);

interface PopoverCompoundedComponent
  extends React.ForwardRefExoticComponent<PopoverProps> {
  Trigger: typeof PopoverTrigger;
  Content: typeof PopoverContent;
}

const Popover = PopoverContainer as PopoverCompoundedComponent;
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;

export default Popover;
